import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';

export type Tema = 'claro' | 'oscuro';

const CLAVE = 'procom-tema';

/** Lee la preferencia guardada. Default: claro (el oscuro solo si el usuario lo eligió). */
function leerInicial(): Tema {
  if (typeof localStorage === 'undefined') return 'claro';
  try {
    return localStorage.getItem(CLAVE) === 'oscuro' ? 'oscuro' : 'claro';
  } catch {
    return 'claro';
  }
}

function aplicar(tema: Tema) {
  if (typeof document === 'undefined') return;
  document.documentElement.classList.toggle('dark', tema === 'oscuro');
  // El favicon queda fuera del tema de la app a propósito: lo elige el navegador
  // con su propio prefers-color-scheme (favicon.svg + los <link media> del
  // index.html). Así el ícono combina con la barra de pestañas, que sigue al
  // sistema y no a la app.
}

type Ctx = { tema: Tema; alternar: () => void; setTema: (t: Tema) => void };

const TemaCtx = createContext<Ctx | null>(null);

export function TemaProvider({ children }: { children: ReactNode }) {
  const [tema, setTema] = useState<Tema>(leerInicial);

  useEffect(() => {
    aplicar(tema);
    try {
      localStorage.setItem(CLAVE, tema);
    } catch {
      /* almacenamiento no disponible: se aplica igual en memoria */
    }
  }, [tema]);

  const alternar = () => setTema((t) => (t === 'claro' ? 'oscuro' : 'claro'));

  return (
    <TemaCtx.Provider value={{ tema, alternar, setTema }}>
      {children}
    </TemaCtx.Provider>
  );
}

/**
 * Privado a propósito: hoy el único consumidor es `ToggleTema`, acá abajo.
 *
 * Exportarlo hacía que este archivo mezclara componentes con un hook, y eso
 * rompe el fast refresh de Vite (`react-refresh/only-export-components`): al
 * editar el archivo se recarga el módulo entero en vez de refrescar el
 * componente. Si alguna pantalla necesita el tema, lo que corresponde es mover
 * el contexto y el hook a su propio archivo, no volver a exportarlo desde acá.
 */
function useTema(): Ctx {
  const ctx = useContext(TemaCtx);
  if (!ctx) throw new Error('useTema debe usarse dentro de <TemaProvider>');
  return ctx;
}

/** Botón compacto para alternar el tema. Pensado para el pie del sidebar. */
export function ToggleTema() {
  const { tema, alternar } = useTema();
  const oscuro = tema === 'oscuro';
  const etiqueta = oscuro ? 'Cambiar a tema claro' : 'Cambiar a tema oscuro';
  return (
    <button
      onClick={alternar}
      title={etiqueta}
      aria-label={etiqueta}
      className="grid h-8 w-8 flex-none place-items-center rounded-md text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800"
    >
      <i className={`ph ${oscuro ? 'ph-sun' : 'ph-moon'} text-[17px]`} />
    </button>
  );
}
